import React, { useMemo, useState } from 'react';
import { isoWeek, isoWeekYear, parseISODate } from '../utils/dates';

// Filters shown above a Historial table: Año, Semana, Hacienda and Exportadora.
// Año and Semana open on the current ISO week (the numbering the backend stores).
const TODAY = new Date();
const CURRENT_YEAR = String(isoWeekYear(TODAY));
const CURRENT_WEEK = String(isoWeek(TODAY));

// Year an item's ISO week belongs to (matches its stored `week`, also across New Year).
export const isoYearOfDate = (iso) => String(isoWeekYear(parseISODate(iso)));

const uniqueSorted = (values, compare) => [...new Set(values)].sort(compare);
const byNumberDesc = (a, b) => b - a;
const byText = (a, b) => a.localeCompare(b);

// `get` reads each item: { year(item), week(item), farm(item), exporters(item) → [names] }.
// Pass a module-level object so the memos below stay stable.
export function useHistoryFilters(items, get) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [week, setWeek] = useState(CURRENT_WEEK);
  const [farm, setFarm] = useState('');
  const [exporter, setExporter] = useState('');

  const options = useMemo(() => {
    // Weeks with items in the chosen year, plus the current week and the selected one.
    const weeks = items.filter((it) => !year || get.year(it) === year).map((it) => String(get.week(it)));
    if (!year || year === CURRENT_YEAR) weeks.push(CURRENT_WEEK);
    if (week) weeks.push(week);
    return {
      years: uniqueSorted([CURRENT_YEAR, ...items.map(get.year), year].filter(Boolean), byNumberDesc),
      weeks: uniqueSorted(weeks, byNumberDesc),
      farms: uniqueSorted(items.map(get.farm).filter(Boolean), byText),
      exporters: uniqueSorted(items.flatMap(get.exporters).filter(Boolean), byText),
    };
  }, [items, get, year, week]);

  const filtered = useMemo(() => items.filter((it) => (
    (!year || get.year(it) === year)
    && (!week || String(get.week(it)) === week)
    && (!farm || get.farm(it) === farm)
    && (!exporter || get.exporters(it).includes(exporter))
  )), [items, get, year, week, farm, exporter]);

  const select = (label, allLabel, value, onChange, values, format = (v) => v) => (
    <select className="history-select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="">{allLabel}</option>
      {values.map((v) => <option key={v} value={v}>{format(v)}</option>)}
    </select>
  );

  const bar = (
    <div className="history-filters">
      {select('Año', 'Todos los Años', year, setYear, options.years)}
      {select('Semana', 'Todas las Semanas', week, setWeek, options.weeks, (w) => `Semana ${w}`)}
      {select('Hacienda', 'Todas las Haciendas', farm, setFarm, options.farms)}
      {select('Exportadora', 'Todas las Exportadoras', exporter, setExporter, options.exporters)}
    </div>
  );

  return { filtered, bar };
}
